import React, { useState, useEffect, forwardRef } from 'react';
import { attributeService } from '../services';
import {
  Checkbox,
  CustomPropertiesForm,
  formatSelectOptions,
  FormTabs,
  Input,
  InputStatus,
  Select,
  Textarea,
  textareaInitialState,
  textareaToRaw,
} from '../components';
import { useCustomProperties, useGlobalState, useMessage, useSearch } from '../hooks';
import { Asset, canHaveAttributeValues, getBatchEditValue } from '../helpers';

const _AddAttributeValueForm = (
  { mode, data, submitForm, loading, setLoading, formErrors = {} },
  ref
) => {
  const initialFormState = {
    attribute_id: null,
    attribute_value_name: '',
    attribute_value_description: textareaInitialState({}),
    attribute_value_code: '',
    status_id: '',
  };

  const initialBatchEditItems = {
    batch_attribute_id: false,
    batch_status_id: false,
  };
  const [currentAttribute] = useGlobalState('currentAttribute');
  const [loggedInUser] = useGlobalState('loggedInUser');
  const [displayNames] = useGlobalState('displayNames');
  const [statuses] = useGlobalState('statuses');
  const { hideMessage } = useMessage();
  const [loadingMoreOptions, setLoadingMoreOptions] = useState(false);
  const [batchEdit, setBatchEdit] = useState(false);
  const [batchEditItems, setBatchEditItems] = useState(initialBatchEditItems);
  const [formData, setFormData] = useState(initialFormState);
  const [defaultSelectedTab, setDefaultSelectedTab] = useState();

  // Attributes
  const [fdRequestResults, setFdRequestResults] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [selectedAttribute, setSelectedAttribute] = useState();

  const fd = useSearch({
    setLoading: setLoadingMoreOptions,
    objectName: 'attribute',
    pagination: true,
    enableHistory: false,
    useDefaultValues: true,
  });

  const {
    batchEditFormCustomPropertyValues,
    batchEditItemsFormCustomPropertyValues,
    customProperties,
    formCustomPropertyValues,
    getCustomPropertyValuesForSubmit,
    hasCustomProperties,
    resetFormCustomPropertyValues,
    setFormCustomPropertyValues,
  } = useCustomProperties({
    objectName: Asset.AttributeValue,
    objectIds: data.map(x => x.attribute_value_id),
  });

  useEffect(() => {
    // Get form data
    fetchAttributes();
  }, []);

  const fetchAttributes = () => {
    attributeService.getAll().then(res => {
      if (res) {
        // Only work with Attributes that have role = "Dimension"
        setAttributes(res.attributes.filter(canHaveAttributeValues));
      }
    });
    return;
  };

  useEffect(() => {
    setBatchEdit(false);
    if (data.length === 1) {
      // copy + edit
      setFormData({
        attribute_value_id: data[0].attribute_value_id,
        attribute_id: data[0].attribute_id,
        attribute_value_name: data[0].attribute_value_name,
        attribute_value_description: textareaInitialState({
          value: data[0].attribute_value_description,
        }),
        attribute_value_code: data[0].attribute_value_code,
        status_id: data[0].status_id,
      });
    } else if (data.length > 1 && mode !== 'add') {
      // bulk edit
      setBatchEdit(true);
      setFormData({
        attribute_id: getBatchEditValue(data, 'attribute_id'),
        status_id: getBatchEditValue(data, 'status_id'),
      });
    } else if (mode === 'add') {
      setFormData({
        ...formData,
        status_id: statuses.default_glossary_status_id,
        attribute_id: currentAttribute ? currentAttribute.attribute_id : null,
      });
    } else {
      resetForm();
    }
  }, [mode, data, loggedInUser, currentAttribute]);

  useEffect(() => {
    let result = fd.searchResults.reduce((res, result) => {
      // Only display attribute role = "Dimension"
      if (canHaveAttributeValues(result.searchable)) {
        res.push({
          value: result.searchable.attribute_id,
          label: result.searchable.attribute_name,
          icon: result.searchable.attribute_role_name,
          tooltip: result.searchable.attribute_description,
        });
      }
      return res;
    }, []);

    setFdRequestResults(result);
  }, [fd.searchResults]);

  const resetForm = () => {
    resetFormCustomPropertyValues();
    setBatchEdit(false);
    setBatchEditItems(initialBatchEditItems);
    setDefaultSelectedTab({ tab: 'general' });
    setFormData(() => {
      return {
        ...initialFormState,
        // The dialog is reset after each save so another value can be added, so the attribute the
        // dialog was opened for has to survive it. It is read from the row the dialog carries, which
        // is where "Add Child" and "Add" both put it — the attribute being viewed is only the
        // fallback, and a list of attributes has no current one.
        attribute_id: data[0]?.attribute_id ?? currentAttribute?.attribute_id ?? null,
        status_id: statuses.default_glossary_status_id,
      };
    });
  };

  const handleSearchAttributeInputChange = value => {
    fd.setSearchTerm(value);
  };

  const onSubmit = event => {
    event.preventDefault();
    setLoading(true);
    let dataToSubmit;

    if (batchEdit) {
      let key = '';
      let tmp = {};
      const objects = Object.entries(batchEditItems);

      for (let i = 0; i < objects.length; i++) {
        if (objects[i][1]) {
          key = objects[i][0].replace('batch_', '');
          tmp = { ...tmp, [key]: formData[key] };
        }
      }

      dataToSubmit = data.map(item => {
        return {
          attribute_value_id: item.attribute_value_id,
          attribute_value_name: item.attribute_value_name,
          attribute_value_description: item.attribute_value_description,
          attribute_value_code: item.attribute_value_code,
          attribute_id: item.attribute_id,
          status_id: item.status_id,
          ...tmp,
          ...getCustomPropertyValuesForSubmit(item.attribute_value_id),
        };
      });
    } else {
      dataToSubmit = {
        ...formData,
        attribute_value_description: textareaToRaw({
          value: formData.attribute_value_description,
        }),
        ...getCustomPropertyValuesForSubmit(formData.attribute_value_id),
      };
    }

    submitForm(dataToSubmit)
      .then(() => resetForm())
      .catch(() => {
        //Errors handled in submitForm function
      })
      .finally(() => setLoading(false));
  };

  const handleInputChange = (event, formPart) => {
    let { name, value } = '';
    if (event.target !== undefined) {
      name = event.target.name;
      value = event.target.value;

      // If input element is a checkbox, we cannot use "value"
      if (event.target.type === 'checkbox') {
        value = event.target.checked;
      }
    } else {
      name = event.name;
      value = event.value;
    }

    switch (name) {
      case 'attribute_id':
        setSelectedAttribute(attributes.find(x => x.attribute_id === value));
        break;
      default:
        break;
    }

    switch (formPart) {
      case 'batch':
        setBatchEditItems({ ...batchEditItems, [name]: value });
        break;
      default:
        setFormData({ ...formData, [name]: value });
    }

    // Hide errors on input change
    if (formErrors && formErrors[name]) {
      delete formErrors[name];
      hideMessage();
    }
  };

  /**
   * Checks for the Attribute to display in the <Select> component
   * Since the form can be triggered from different locations, the current option is set differently depending on the situation
   * - Selected Attribute: used when an option is Selected from the <Select> component
   * - Current Attribute: used when the form is triggered from the AttributeValue view
   * - attribute: used when the form is triggered from the Attributes view and no currentAttribute exists.
   */
  const getOptions = () => {
    let attribute = null;
    if (formData.attribute_id) {
      attribute = attributes.find(x => x.attribute_id === formData.attribute_id);
    }

    return selectedAttribute
      ? [
          {
            attribute_id: selectedAttribute.attribute_id,
            attribute_name: selectedAttribute.attribute_name,
            attribute_role_name: selectedAttribute.attribute_role_name,
            attribute_description: selectedAttribute.attribute_description,
          },
        ]
      : currentAttribute
        ? [currentAttribute]
        : attribute
          ? [attribute]
          : [];
  };

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>
        <div className="form-block vertical">
          <InputStatus
            name="attribute_value_name"
            label="Name"
            value={formData.attribute_value_name}
            statusValue={formData.status_id}
            assetCategory="glossary"
            onChange={handleInputChange}
            loading={loading}
            batchEdit={batchEdit}
            batchEditItems={batchEditItems}
            validation={
              formErrors.attribute_value_name
                ? { valid: false, message: formErrors.attribute_value_name }
                : undefined
            }
          />
        </div>

        <FormTabs
          defaultSelectedTab={defaultSelectedTab}
          formErrors={formErrors}
          hideTabsIfOnlyOne={true}
        >
          <div label="General">
            {!batchEdit && (
              <React.Fragment>
                <div className="form-block vertical">
                  <Textarea
                    name="attribute_value_description"
                    value={formData.attribute_value_description}
                    onChange={handleInputChange}
                    disabled={loading}
                    isOptional={true}
                  />
                </div>
                <div className="form-block vertical">
                  <Input
                    type="text"
                    name="attribute_value_code"
                    value={formData.attribute_value_code || ''}
                    onChange={handleInputChange}
                    title={displayNames.attribute_value_code.display_name_description}
                    disabled={loading}
                    validation={
                      formErrors.attribute_value_code
                        ? { valid: false, message: formErrors.attribute_value_code }
                        : undefined
                    }
                    isOptional={true}
                  />
                </div>
              </React.Fragment>
            )}
            <div className="form-block vertical">
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_attribute_id}
                    name="batch_attribute_id"
                    label="Edit Attribute"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  ></Checkbox>
                </label>
              )}
              <Select
                name="attribute_id"
                label={!batchEdit}
                value={formData.attribute_id === 'multiple' ? undefined : formData.attribute_id}
                options={[
                  // Before searching, keep the current/selected attribute visible
                  // alongside the default results (deduped); while searching, show only the
                  // search results. The defaults are fetched on focus via fetchDefaultSearchResults.
                  ...(fd.searchTerm
                    ? []
                    : formatSelectOptions({
                        options: getOptions(),
                        optionValue: 'attribute_id',
                        optionLabel: 'attribute_name',
                        optionIcon: 'attribute_role_name',
                        tooltip: 'attribute_description',
                      }).filter(option => !fdRequestResults.some(r => r.value === option.value))),
                  ...fdRequestResults,
                ]}
                onInputChange={handleSearchAttributeInputChange}
                onFocus={() => fd.fetchDefaultSearchResults()}
                loadMoreOptions={fd.loadNextPage}
                loading={fd.loadingMoreOptions}
                searchTerm={fd.searchTerm}
                clearSearchResults={fd.clearSearchResults}
                placeholder={
                  formData.attribute_id === 'multiple' ? '< Multiple >' : 'Search Attribute ...'
                }
                isClearable={true}
                isNullable={false}
                onChange={handleInputChange}
                disabled={loading || (batchEdit && !batchEditItems.batch_attribute_id)}
              ></Select>
            </div>
          </div>
          {hasCustomProperties && (
            <div label="More">
              <CustomPropertiesForm
                customProperties={customProperties}
                formData={formCustomPropertyValues}
                batchEdit={batchEditFormCustomPropertyValues}
                batchEditItems={batchEditItemsFormCustomPropertyValues}
                onChange={setFormCustomPropertyValues}
                loading={loading}
              />
            </div>
          )}
        </FormTabs>
      </form>
    </div>
  );
};
export const AddAttributeValueForm = forwardRef(_AddAttributeValueForm);
